import React, { FC, useEffect, useRef, useState, useMemo } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useNavigate } from 'react-router-dom';
import { Unity, useUnityContext } from 'react-unity-webgl';

import Lottie from 'lottie-react';
import { ReactUnityEventParameter } from 'react-unity-webgl/distribution/types/react-unity-event-parameters';

import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';

import catAnim from '@assets/lottie/catAnim.json';

import { useResponsiveUnity } from './hooks';
import { UnityComponentProps } from './types';

import './index.scss';

// Добавляем Google Font
// const fontStyle = {
//   fontFamily: "'Comfortaa', sans-serif",
//   fontWeight: 700,
//   fontSize: '32px',
// };

const getLoadingText = (percentage: number): string => {
  // if (percentage < 25) return 'Собираем игру для вас с любовью';
  // if (percentage < 50) return 'Загружаем игровые ресурсы';
  if (percentage < 90) return 'Downloading game files...';
  return 'Game files loaded, starting preparation...';
};

export const UnityComponent: FC<UnityComponentProps> = ({
  loaderUrl,
  dataUrl,
  frameworkUrl,
  isFullScreenEnabled = false,
  codeUrl,
  resolution,
  unityProductVersion,
  onReady,
  onProgress,
}) => {
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  const [unityError, setUnityError] = useState<string | null>(null);
  const [fileLoadErrors, setFileLoadErrors] = useState<Record<string, string>>({});
  const [failedFileUrls, setFailedFileUrls] = useState<Set<string>>(new Set());
  const [criticalFilesFailed, setCriticalFilesFailed] = useState<Set<string>>(new Set()); // data, framework, wasm
  const isMountedRef = useRef(true);
  const loadedUrlsRef = useRef<Set<string>>(new Set()); // Отслеживаем загруженные URL
  const isLoadingRef = useRef(false); // Защита от двойной загрузки
  const fileSizesFetchedRef = useRef<Set<string>>(new Set()); // Отслеживаем уже полученные размеры для этих URL

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // УБРАНО: Отслеживание активных загрузок через PerformanceObserver
  // УБРАНО: Функция checkActiveDownloads
  // УБРАНО: Функция autoClearCacheAndReload
  // Версия из index.html используется для очистки кэша при изменении (в Game/index.tsx)

  // Очистка кэша ТОЛЬКО при изменении productVersion из index.html
  const prevVersionRef = useRef<string | null>(null);

  // Создаем стабильный ключ для отслеживания загрузки на основе всех URL
  const unityUrlsKey = useMemo(() => {
    if (!loaderUrl || !dataUrl || !frameworkUrl || !codeUrl) return '';
    return `${loaderUrl}|${dataUrl}|${frameworkUrl}|${codeUrl}`;
  }, [loaderUrl, dataUrl, frameworkUrl, codeUrl]);

  // Проверяем, не загружается ли уже игра с теми же URL
  useEffect(() => {
    if (!unityUrlsKey) return;
    
    if (loadedUrlsRef.current.has(unityUrlsKey)) {
      console.log('🛡️ [UnityComponent] Игра с этими URL уже загружена, пропускаем повторную загрузку');
      return;
    }
    
    if (isLoadingRef.current) {
      console.log('🛡️ [UnityComponent] Игра уже загружается, пропускаем повторную загрузку');
      return;
    }
    
    // Помечаем, что начинаем загрузку
    isLoadingRef.current = true;
    
    // Очищаем флаг загрузки после небольшой задержки (Unity сам управляет загрузкой)
    const timeout = setTimeout(() => {
      isLoadingRef.current = false;
    }, 1000);
    
    return () => {
      clearTimeout(timeout);
    };
  }, [unityUrlsKey]);

  // Очистка кэша ТОЛЬКО при изменении productVersion (объединено с получением размеров файлов ниже)
  useEffect(() => {
    if (unityProductVersion && unityProductVersion !== prevVersionRef.current && loaderUrl && dataUrl && frameworkUrl && codeUrl) {
      console.log(`🔄 [UnityComponent] Cache clearing: version changed ${prevVersionRef.current || 'none'} → ${unityProductVersion}`);
      
      // Очищаем Service Worker кэш если есть
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          const unityCacheNames = cacheNames.filter(name => 
            name.includes('unity') || name.includes('webgl') || name.includes('game')
          );
          if (unityCacheNames.length > 0) {
            Promise.all(unityCacheNames.map(name => caches.delete(name))).then(() => {
              console.log('🧹 Cleared Unity caches:', unityCacheNames);
            });
          }
        });
      }
      
      // Сбрасываем кэш размеров файлов, чтобы они перезапросились для новой версии
      fileSizesFetchedRef.current.clear();
      console.log('🔄 [UnityComponent] Cleared file sizes cache for new version');
      
      prevVersionRef.current = unityProductVersion;
    }
  }, [unityProductVersion, loaderUrl, dataUrl, frameworkUrl, codeUrl]);

  // Используем useMemo для стабильности конфигурации Unity
  const unityConfig = useMemo(() => {
    // Если URL еще не готовы, возвращаем пустые строки
    if (!loaderUrl || !dataUrl || !frameworkUrl || !codeUrl) {
      return {
        loaderUrl: '',
        dataUrl: '',
        frameworkUrl: '',
        codeUrl: '',
        autoSyncPersistentDataPath: true,
      };
    }
    
    return {
      loaderUrl,
      dataUrl,
      frameworkUrl,
      codeUrl,
      autoSyncPersistentDataPath: true,
    };
  }, [loaderUrl, dataUrl, frameworkUrl, codeUrl]);

  const unityContext = useUnityContext(unityConfig);

  useResponsiveUnity(resolution);

  const {
    unityProvider,
    loadingProgression,
    isLoaded,
    addEventListener,
    removeEventListener,
  } = unityContext;

  // Отслеживаем успешную загрузку Unity
  useEffect(() => {
    if (isLoaded && unityUrlsKey) {
      console.log('✅ [UnityComponent] Unity игра успешно загружена');
      loadedUrlsRef.current.add(unityUrlsKey);
      isLoadingRef.current = false;

      // Сообщаем родителю о готовности
      if (onReady) {
        onReady();
      }
    }
  }, [isLoaded, unityUrlsKey, onReady]);



  const handle = useFullScreenHandle();

  const navigate = useNavigate();

  const reloadPage = () => {
    navigate('/game');
  };

  useEffect(() => {
    addEventListener('ExitGame', reloadPage as () => ReactUnityEventParameter);

    return () => {
      removeEventListener(
        'ExitGame',
        reloadPage as () => ReactUnityEventParameter
      );
    };
  }, [addEventListener, removeEventListener]);

  useEffect(() => {
    const updateDevicePixelRatio = function () {
      setDevicePixelRatio(window.devicePixelRatio);
    };
    // Используем начальное значение devicePixelRatio только для создания mediaMatcher
    // Не добавляем devicePixelRatio в зависимости, чтобы избежать бесконечного цикла
    const initialDevicePixelRatio = window.devicePixelRatio;
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${initialDevicePixelRatio}dppx)`
    );

    mediaMatcher.addEventListener('change', updateDevicePixelRatio);

    return () => {
      mediaMatcher.removeEventListener('change', updateDevicePixelRatio);
    };
  }, []); // Пустой массив зависимостей - эффект запускается только один раз

  // ОТКЛЮЧЕНО: Проверка доступности Unity файлов перед загрузкой
  // Эта проверка создаёт лишние запросы и CORS ошибки
  // Unity сам проверит доступность файлов при загрузке
  // useEffect(() => {
  //   const checkFilesAvailability = async () => {
  //     ...
  //   };
  //   if (loaderUrl && dataUrl && frameworkUrl && codeUrl) {
  //     checkFilesAvailability();
  //   }
  // }, [loaderUrl, dataUrl, frameworkUrl, codeUrl]);

  // Глобальный обработчик ошибок для WebGPU и сетевых ошибок
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      const errorMessage = event.error?.message || event.message || '';
      const errorString = errorMessage.toLowerCase();
      
      // Проверяем ошибки декомпрессии GZ файлов (отсутствие Content-Encoding заголовка)
      if (errorString.includes('content-encoding') || 
          errorString.includes('corrupt') || 
          errorString.includes('compression was misconfigured') ||
          errorString.includes('unable to parse') ||
          errorString.includes('malformed data') ||
          errorString.includes('still gzip-compressed')) {
        // Проверяем, что это ошибка для .gz файла
        const target = event.target as HTMLElement;
        const src = target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')
          ? ((target as HTMLScriptElement).src || (target as HTMLLinkElement).href)
          : '';
        
        if (src && src.includes('.gz')) {
          console.error('❌ [UnityComponent] GZ file decompression error:', src);
          console.error('📦 [UnityComponent] Reason:', errorMessage);
          console.error('📦 [UnityComponent] Server is not sending Content-Encoding: gzip header for .gz files');
          console.error('📦 [UnityComponent] Unity loader cannot decompress file without proper header');
          setFailedFileUrls(prev => new Set(prev).add(src));
          
          // Показываем ошибку пользователю
          if (isMountedRef.current) {
            setUnityError('Game loading error: server is not configured for automatic GZ file decompression. Please contact administrator.');
          }
          return;
        }
      }
      
      console.error('🌐 Global error:', event.error);
      
      if (event.error && event.error.message) {
        const message = event.error.message.toLowerCase();
        
        if (message.includes('webgpu') || message.includes('wgpu') || message.includes('graphics api')) {
          console.warn('WebGPU error detected:', event.error);
          if (isMountedRef.current) {
            setUnityError('Your device does not support modern graphics technologies. Please try updating your browser or use another device.');
          }
        } else if (message.includes('network') || message.includes('fetch') || message.includes('failed to fetch') || message.includes('404') || message.includes('not found')) {
          console.error('Network error detected:', event.error);
          // Проверяем, что это ошибка загрузки Unity файла
          const target = event.target as HTMLElement;
          if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
            const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;
            if (src && (src.includes('.br') || src.includes('.gz') || src.includes('loader.js') || src.includes('framework.js') || src.includes('data') || src.includes('wasm'))) {
              // Проверяем, является ли это критическим файлом (data, framework, wasm)
              const isCriticalFile = src.includes('framework') || src.includes('data') || src.includes('wasm');
              const isCompressionError = errorString.includes('content-encoding') || 
                                         errorString.includes('corrupt') || 
                                         errorString.includes('compression was misconfigured') ||
                                         errorString.includes('unable to parse') ||
                                         errorString.includes('malformed data') ||
                                         errorString.includes('still gzip-compressed');
              
              if (src.includes('.gz')) {
                console.error(`❌ [UnityComponent] GZ file loading error: ${src}`);
                console.error(`📦 [UnityComponent] Error reason: ${message}`);
                if (message.includes('404') || message.includes('not found')) {
                  console.error('📦 [UnityComponent] GZ file not found on server (404)');
                  // Если это критический файл и не ошибка компрессии, отмечаем его
                  if (isCriticalFile && !isCompressionError) {
                    setCriticalFilesFailed(prev => new Set(prev).add(src));
                  }
                } else if (message.includes('failed to fetch')) {
                  console.error('📦 [UnityComponent] Failed to load GZ file (network error)');
                  console.log('⏳ [UnityComponent] Waiting for connection recovery for automatic reload...');
                  // Если это критический файл и не ошибка компрессии, отмечаем его
                  if (isCriticalFile && !isCompressionError) {
                    setCriticalFilesFailed(prev => new Set(prev).add(src));
                  }
                }
              } else {
              // Не GZ файл - это точно ошибка загрузки, а не компрессии
                if (isCriticalFile) {
                  setCriticalFilesFailed(prev => new Set(prev).add(src));
                }
              }
              setFailedFileUrls(prev => new Set(prev).add(src));
            }
          }
          // Не показываем общую сетевую ошибку, если есть ошибки критических файлов
          // (специфичная ошибка будет показана через useEffect для criticalFilesFailed)
          // Проверяем синхронно, есть ли критический файл в текущей ошибке
          const hasCriticalFileInError = target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK') && 
            ((target as HTMLScriptElement).src || (target as HTMLLinkElement).href || '').match(/(framework|data|wasm)/);
          if (!hasCriticalFileInError) {
            // Не показываем ошибку сразу, если интернет может восстановиться
            if (isMountedRef.current && !navigator.onLine) {
              setUnityError('Internet connection lost. Waiting for reconnection...');
            } else if (isMountedRef.current) {
              setUnityError('Network error while loading the game. Please check your internet connection.');
            }
          }
        } else if (message.includes('timeout') || message.includes('timed out')) {
          console.error('Timeout error detected:', event.error);
          if (isMountedRef.current) {
            setUnityError('Loading timeout exceeded. Please try reloading the page or clearing your browser cache.');
          }
        }
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorMessage = event.reason?.message || event.reason?.toString() || '';
      const errorString = errorMessage.toLowerCase();
      
      // Проверяем ошибки декомпрессии GZ файлов
      if (errorString.includes('content-encoding') || 
          errorString.includes('corrupt') || 
          errorString.includes('compression was misconfigured') ||
          errorString.includes('unable to parse') ||
          errorString.includes('malformed data') ||
          errorString.includes('still gzip-compressed')) {
        console.error('❌ [UnityComponent] GZ decompression error in promise rejection:', event.reason);
        console.error('📦 [UnityComponent] Reason:', errorMessage);
        console.error('📦 [UnityComponent] Server is not sending Content-Encoding: gzip header for .gz files');
        console.error('📦 [UnityComponent] Unity loader cannot decompress file without proper header');
        if (isMountedRef.current) {
          setUnityError('Game loading error: server is not configured for automatic GZ file decompression. Please contact administrator.');
        }
        return;
      }
      
      console.error('🌐 Unhandled promise rejection:', event.reason);
      
      if (event.reason && event.reason.message) {
        const message = event.reason.message.toLowerCase();
        
        if (message.includes('webgpu') || message.includes('wgpu')) {
          console.warn('WebGPU promise rejection:', event.reason);
          if (isMountedRef.current) {
            setUnityError('Your device does not support modern graphics technologies. Please try updating your browser or use another device.');
          }
        } else if (message.includes('network') || message.includes('fetch')) {
          console.error('Network promise rejection:', event.reason);
          // Проверяем, связана ли ошибка с GZ файлами
          const reasonString = String(event.reason || '');
          const isCriticalFile = reasonString.includes('framework') || reasonString.includes('data') || reasonString.includes('wasm');
          const isCompressionError = errorString.includes('content-encoding') || 
                                     errorString.includes('corrupt') || 
                                     errorString.includes('compression was misconfigured') ||
                                     errorString.includes('unable to parse') ||
                                     errorString.includes('malformed data') ||
                                     errorString.includes('still gzip-compressed');
          
          if (reasonString.includes('.gz')) {
            console.error(`❌ [UnityComponent] GZ file loading error in promise rejection`);
            console.error(`📦 [UnityComponent] Error reason: ${errorMessage}`);
            // Если это критический файл и не ошибка компрессии, отмечаем его
            if (isCriticalFile && !isCompressionError) {
              // Пытаемся извлечь URL из ошибки
              const urlMatch = reasonString.match(/https?:\/\/[^\s]+/);
              if (urlMatch) {
                setCriticalFilesFailed(prev => new Set(prev).add(urlMatch[0]));
              }
            }
          } else if (isCriticalFile && !isCompressionError) {
            // Не GZ файл, но критический - это ошибка загрузки
            const urlMatch = reasonString.match(/https?:\/\/[^\s]+/);
            if (urlMatch) {
              setCriticalFilesFailed(prev => new Set(prev).add(urlMatch[0]));
            }
          }
          // Не показываем общую сетевую ошибку, если есть ошибки критических файлов
          // (специфичная ошибка будет показана через useEffect для criticalFilesFailed)
          const hasCriticalErrors = criticalFilesFailed.size > 0;
          if (!hasCriticalErrors) {
            // Не показываем ошибку сразу, если интернет может восстановиться
            if (isMountedRef.current && !navigator.onLine) {
              setUnityError('Internet connection lost. Waiting for reconnection...');
            } else if (isMountedRef.current) {
              setUnityError('Network error while loading the game. Please check your internet connection.');
            }
          }
        }
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Отслеживаем реальный прогресс загрузки файлов через Performance API
  const [realProgress, setRealProgress] = useState(0);
  const [fileSizes, setFileSizes] = useState<Record<string, number>>({}); // Реальные размеры файлов
  const [fileWeights, setFileWeights] = useState<Record<string, number>>({}); // Веса файлов на основе реальных размеров
  const [loadedSize, setLoadedSize] = useState(0); // Загруженный размер в байтах
  const [isOnline, setIsOnline] = useState(navigator.onLine); // Статус подключения к интернету
  
  // Используем фиксированные веса файлов вместо HEAD запросов для ускорения загрузки
  useEffect(() => {
    if (!loaderUrl && !dataUrl && !frameworkUrl && !codeUrl) return;

    // Используем фиксированные веса, основанные на типичных размерах Unity файлов
    const weights: Record<string, number> = {
      'loader.js': 0.05,
      'framework.js': 0.25,
      'data': 0.60,
      'wasm': 0.10,
    };

    setFileWeights(weights);
  }, [loaderUrl, dataUrl, frameworkUrl, codeUrl]);
  
  useEffect(() => {
    if (!loaderUrl && !dataUrl && !frameworkUrl && !codeUrl) return;
    if (Object.keys(fileWeights).length === 0) return; // Ждем получения весов
    
    const unityFiles = [
      { url: loaderUrl, name: 'loader.js' },
      { url: frameworkUrl, name: 'framework.js' },
      { url: dataUrl, name: 'data' },
      { url: codeUrl, name: 'wasm' },
    ].filter(file => file.url);
    
    const fileProgress: Record<string, number> = {};
    
    // Функция для вычисления прогресса на основе Performance API с реальными весами
    const calculateProgress = () => {
      if (!('performance' in window) || !('getEntriesByType' in performance)) {
        return { progress: 0, loadedBytes: 0 };
      }
      
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      let totalProgress = 0;
      let totalWeight = 0;
      let totalLoadedBytes = 0; // Общий загруженный размер в байтах
      
      unityFiles.forEach(({ url, name }) => {
        const weight = fileWeights[name] || 0;
        if (weight === 0) return; // Пропускаем файлы без веса
        
        // Ищем ВСЕ ресурсы с таким именем файла (без query параметров)
        // Файл может загружаться несколько раз (HEAD запрос для проверки размера + GET для загрузки)
        // Берем ресурс с максимальным размером (обычно это GET запрос)
        const fileName = url.split('/').pop()?.split('?')[0] || '';
        const matchingResources = resources.filter(r => {
          const resourceName = r.name.split('?')[0];
          return resourceName.endsWith(fileName) || resourceName.includes(fileName);
        });
        
        // Если найдено несколько ресурсов, берем тот, который загрузил больше всего данных
        // Обычно это GET запрос (transferSize > 0), а HEAD запрос имеет transferSize = 0
        if (matchingResources.length > 0) {
          // Берем ресурс с максимальным transferSize или encodedBodySize (это обычно GET запрос)
          const resource = matchingResources.reduce((max, r) => {
            const maxSize = (max as any).encodedBodySize || max.transferSize || max.decodedBodySize || 0;
            const rSize = (r as any).encodedBodySize || r.transferSize || r.decodedBodySize || 0;
            return rSize > maxSize ? r : max;
          });
          
          // Берем максимальный размер из всех запросов (чтобы не считать HEAD запросы)
          // HEAD запросы обычно имеют transferSize = 0, поэтому мы берем GET запрос
          let totalBytesForThisFile = 0;
          matchingResources.forEach((r) => {
            const rTransferSize = r.transferSize || 0;
            const rEncodedSize = (r as any).encodedBodySize || 0;
            const rDecodedSize = r.decodedBodySize || 0;
            const rSize = rEncodedSize > 0 ? rEncodedSize : (rTransferSize > 0 ? rTransferSize : rDecodedSize);
            // Берем максимальный размер из всех запросов (чтобы не считать HEAD запросы дважды)
            if (rSize > totalBytesForThisFile) {
              totalBytesForThisFile = rSize;
            }
          });
          
          // УБРАНО: Предупреждение о дублировании загрузок
          // Логи были слишком частыми и мешали разработке
          
          if (resource) {
            // Используем максимальный размер из всех запросов для этого файла
            const loadedBytesForFile = totalBytesForThisFile;
            
            // Получаем размеры из основного ресурса для расчета прогресса
            const transferSize = resource.transferSize || 0;
            const decodedBodySize = resource.decodedBodySize || 0;
            const encodedBodySize = (resource as any).encodedBodySize || 0;
            const expectedSize = fileSizes[name] || encodedBodySize || decodedBodySize || transferSize;
            
            // Если файл завершил загрузку (responseEnd > 0), используем реальный размер из fileSizes
            if (resource.responseEnd > 0) {
              // Файл полностью загружен - используем реальный размер файла из HEAD запроса
              const finalSize = fileSizes[name] || loadedBytesForFile;
              if (finalSize > 0) {
                totalLoadedBytes += finalSize;
                fileProgress[name] = 100;
                totalProgress += weight * 100;
                totalWeight += weight;
              } else {
                // Если размер неизвестен, используем загруженные байты
                totalLoadedBytes += loadedBytesForFile;
                fileProgress[name] = 100;
                totalProgress += weight * 100;
                totalWeight += weight;
              }
            } else if (loadedBytesForFile > 0 && expectedSize > 0) {
              // Файл загружается - используем loadedBytesForFile относительно expectedSize
              totalLoadedBytes += loadedBytesForFile;
              const fileProgressPercent = Math.min((loadedBytesForFile / expectedSize) * 100, 95); // Максимум 95% до завершения
              fileProgress[name] = fileProgressPercent;
              totalProgress += weight * fileProgressPercent;
              totalWeight += weight;
            } else {
              // Файл еще не начал загружаться
              fileProgress[name] = 0;
              totalWeight += weight;
            }
          }
        } else {
          // Ресурс еще не найден в Performance API
          fileProgress[name] = 0;
          totalWeight += weight;
        }
      });
      
      // Нормализуем прогресс по общему весу
      const normalizedProgress = totalWeight > 0 ? (totalProgress / totalWeight) : 0;
      return {
        progress: Math.round(Math.min(normalizedProgress, 99)), // Максимум 99% до полной загрузки
        loadedBytes: totalLoadedBytes
      };
    };
    
    // Используем PerformanceObserver для отслеживания загрузки в реальном времени
    let observer: PerformanceObserver | null = null;

    if ('PerformanceObserver' in window) {
      try {
        observer = new PerformanceObserver((list) => {
          // Проверка перед setState для предотвращения memory leak
          if (isMountedRef.current) {
            const { progress, loadedBytes } = calculateProgress();
            setRealProgress(progress);
            setLoadedSize(loadedBytes);
          }
        });

        observer.observe({ entryTypes: ['resource'] });
      } catch (e) {
        console.warn('PerformanceObserver is not supported:', e);
      }
    }

    // Также проверяем прогресс периодически (fallback если PerformanceObserver не работает)
    const intervalId = setInterval(() => {
      // Проверка перед setState для предотвращения memory leak
      if (isMountedRef.current) {
        const { progress, loadedBytes } = calculateProgress();
        setRealProgress(progress);
        setLoadedSize(loadedBytes);
      }
    }, 200);
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
      clearInterval(intervalId);
    };
    
    // Отслеживаем ошибки загрузки файлов
    const handleScriptError = (event: ErrorEvent) => {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
        const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;
        if (src && (src.includes('.br') || src.includes('.gz') || src.includes('loader.js') || src.includes('framework.js') || src.includes('data') || src.includes('wasm'))) {
          const errorMsg = event.error?.message || event.message || 'Unknown error';
          const errorMsgLower = errorMsg.toLowerCase();
          console.error('❌ [UnityComponent] Unity file loading error:', src);
          
          // Проверяем, является ли это критическим файлом (data, framework, wasm)
          const isCriticalFile = src.includes('framework') || src.includes('data') || src.includes('wasm');
          const isCompressionError = errorMsgLower.includes('content-encoding') || 
                                     errorMsgLower.includes('corrupt') || 
                                     errorMsgLower.includes('compression was misconfigured') ||
                                     errorMsgLower.includes('unable to parse') ||
                                     errorMsgLower.includes('malformed data') ||
                                     errorMsgLower.includes('still gzip-compressed');
          
          if (src.includes('.gz')) {
            console.error(`📦 [UnityComponent] GZ file error reason: ${errorMsg}`);
            console.error(`📦 [UnityComponent] Check that server sends Content-Encoding: gzip header for .gz files`);
            if (isCompressionError) {
              console.error(`📦 [UnityComponent] Problem: server is not configured for automatic GZ file decompression`);
              console.error(`📦 [UnityComponent] Solution: configure server to send Content-Encoding: gzip header`);
            } else if (isCriticalFile) {
              // Это критический файл, но ошибка не компрессии - значит файл не загружается
              setCriticalFilesFailed(prev => new Set(prev).add(src));
            }
          } else {
            console.error(`📦 [UnityComponent] Error reason: ${errorMsg}`);
            // Не GZ файл - это точно ошибка загрузки, а не компрессии
            if (isCriticalFile) {
              setCriticalFilesFailed(prev => new Set(prev).add(src));
            }
          }
          
          setFailedFileUrls(prev => new Set(prev).add(src));
        }
      }
    };
    
    window.addEventListener('error', handleScriptError, true);
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
      clearInterval(intervalId);
      window.removeEventListener('error', handleScriptError, true);
    };
  }, [loaderUrl, dataUrl, frameworkUrl, codeUrl, fileWeights, fileSizes]);

  // Отслеживание состояния интернет-соединения и автоматическое восстановление загрузки
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 [UnityComponent] Internet connection restored');
      setIsOnline(true);
      
      // Если были ошибки загрузки файлов, очищаем их и позволяем Unity loader перезагрузить файлы
      if (failedFileUrls.size > 0 || criticalFilesFailed.size > 0) {
        console.log('🔄 [UnityComponent] Clearing loading errors list for retry');
        setFailedFileUrls(new Set());
        setCriticalFilesFailed(new Set());
        setUnityError(null);
        
        // Unity loader автоматически перезагрузит файлы при изменении URL или пересоздании компонента
        // Но мы можем принудительно очистить кэш для проблемных файлов
        if ('caches' in window) {
          caches.keys().then(cacheNames => {
            cacheNames.forEach(cacheName => {
              if (cacheName.includes('unity') || cacheName.includes('webgl')) {
                caches.delete(cacheName).then(() => {
                  console.log(`🧹 [UnityComponent] Cleared cache: ${cacheName}`);
                });
              }
            });
          });
        }
      }
    };

    const handleOffline = () => {
      console.warn('⚠️ [UnityComponent] Internet connection lost');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [failedFileUrls, criticalFilesFailed]);

  // Отслеживаем ошибки загрузки критических файлов (data, framework, wasm) и показываем ошибку пользователю
  useEffect(() => {
    console.log('🔍 [UnityComponent] criticalFilesFailed changed:', criticalFilesFailed.size, Array.from(criticalFilesFailed));
    console.log('🔍 [UnityComponent] isMountedRef.current:', isMountedRef.current);
    
    if (criticalFilesFailed.size > 0) {
      const failedFilesList = Array.from(criticalFilesFailed);
      console.log('🚨 [UnityComponent] Critical files failed to load:', failedFilesList);
      const fileNames = failedFilesList.map(url => {
        if (url.includes('framework')) return 'framework';
        if (url.includes('data')) return 'data';
        if (url.includes('wasm')) return 'wasm';
        return 'file';
      }).filter((v, i, a) => a.indexOf(v) === i); // Уникальные имена
      
      const filesText = fileNames.join(', ');
      const errorMessage = `Failed to load game files (${filesText}). Please check your internet connection or try reloading the page.`;
      console.log('🚨 [UnityComponent] Setting error message:', errorMessage);
      console.log('🚨 [UnityComponent] isMountedRef.current before setUnityError:', isMountedRef.current);
      
      if (isMountedRef.current) {
        setUnityError(errorMessage);
        console.log('✅ [UnityComponent] Error message set successfully');
      } else {
        console.warn('⚠️ [UnityComponent] Component is not mounted, cannot set error');
      }
    } else {
      console.log('ℹ️ [UnityComponent] No critical files failed, clearing error if exists');
      // Не сбрасываем ошибку автоматически, только если файлы восстановились
    }
  }, [criticalFilesFailed]);

  // Используем реальный прогресс из Performance API, если он доступен
  // Если есть ошибки загрузки или файлы не грузятся, показываем 0%
  // Если реальный прогресс недоступен, используем прогресс от Unity loader
  const hasLoadErrors = failedFileUrls.size > 0 || criticalFilesFailed.size > 0;
  const unityLoaderProgress = Math.round(loadingProgression * 100);
  
  // Проверяем, что файлы действительно загружаются
  // Если нет прогресса ни от Performance API, ни от Unity loader, значит файлы не грузятся
  const filesNotLoading = !hasLoadErrors && realProgress === 0 && unityLoaderProgress === 0;
  
  // Приоритет: ошибки > файлы не грузятся > реальный прогресс > прогресс Unity loader
  const loadingPercentage = (hasLoadErrors || filesNotLoading)
    ? 0 
    : (realProgress > 0 ? realProgress : unityLoaderProgress);

  const { active, exit, enter } = handle;

  // Передаем прогресс загрузки родительскому компоненту
  useEffect(() => {
    if (onProgress && !isLoaded) {
      onProgress(loadingPercentage);
    }
  }, [loadingPercentage, onProgress, isLoaded]);

  const fullScreenTitle = active
    ? 'Нажмите, чтобы выйти из полноэкранного режима'
    : 'Нажмите, чтобы войти в полноэкранный режим';

  // Отладочное логирование перед рендером
  useEffect(() => {
    // console.log('🎨 [UnityComponent] Render - unityError:', unityError);
    // console.log('🎨 [UnityComponent] Render - criticalFilesFailed.size:', criticalFilesFailed.size);
    // console.log('🎨 [UnityComponent] Render - isLoaded:', isLoaded);
  });

  return (
    <FullScreen className="fullScreenContainer" handle={handle}>
      <Box id="unity-container" className="unityContainer">
        <Unity
          unityProvider={unityProvider}
          className="unityGame"
          devicePixelRatio={devicePixelRatio}
        />
        {!isLoaded && (
              <>
                <Box className="loadingContainer">
                  <LinearProgress
                    variant="determinate"
                    value={loadingPercentage}
                    className="linearProgress"
                  />
                  <Typography className="percentageContainer" sx={{
                    fontSize: 'clamp(8px, 2vw, 14px)'
                  }}>{`${loadingPercentage}%`}</Typography>
                </Box>
                <Typography sx={{ 
                  color: 'rgb(198, 115, 124)',
                  textAlign: 'center', 
                  position: 'absolute',
                  bottom: '0%',
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  fontFamily: "'Comfortaa', sans-serif",
                  fontWeight: 200,
                  fontSize: '14px',
                }} className="pulseText">
                  {getLoadingText(loadingPercentage)}
                </Typography>
                {/* Lottie анимация кота снизу по центру */}
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '2%',
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}>
                  <Lottie
                    animationData={catAnim}
                    loop={true}
                    style={{ width: 220, height: 90 }}
                  />
                </Box>
                {/* Индикатор размера загрузки - загружено/всего */}
                {(() => {
                  const totalSize = Object.values(fileSizes).reduce((sum, size) => sum + size, 0);
                  // Используем реальный прогресс для расчета загруженных байт, если loadedSize = 0
                  const effectiveLoadedSize = loadedSize > 0 
                    ? loadedSize 
                    : (totalSize > 0 && realProgress > 0 ? (totalSize * realProgress / 100) : 0);
                  const loadedMB = (effectiveLoadedSize / 1024 / 1024).toFixed(1);
                  const totalMB = totalSize > 0 ? (totalSize / 1024 / 1024).toFixed(1) : '?';
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: '-5px',
                        right: '10px',
                        // backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'rgb(198, 115, 124)',
                        padding: '0px 0px',
                        fontSize: '5px',
                        fontWeight: 200,
                        zIndex: 1000,
                        fontFamily: "'Comfortaa', sans-serif",
                      }}
                      title={`Загружено: ${loadedMB} MB из ${totalMB} MB (${realProgress}%)`}
                    className="pulseText">
                      {loadedMB}/{totalMB}MB
                    </Box>
                  );
                })()}
              </>
            )}
        {isFullScreenEnabled && (
          <Tooltip title={fullScreenTitle} placement="left" enterTouchDelay={0} leaveTouchDelay={3000}>
            <IconButton
              className="fullScreenIconContainer"
              onClick={active ? void exit : void enter}
            >
              {active ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        )}
        {unityError && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(41, 90, 118, 0.85)',
            color: 'white',
            textAlign: 'center',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '90%',
            width: 'auto',
            zIndex: 10000,
            pointerEvents: 'auto'
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Game Loading Error
            </Typography>
            <Typography sx={{ marginBottom: 3, maxWidth: '400px' }}>
              {unityError}
            </Typography>
            {failedFileUrls.size > 0 && (
              <Box sx={{ 
                marginBottom: 2, 
                padding: 2, 
                backgroundColor: 'rgba(255, 0, 0, 0.2)', 
                borderRadius: 1,
                border: '1px solid rgba(255, 0, 0, 0.5)'
              }}>
                <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 'bold', color: 'white' }}>
                  File Loading Errors:
                </Typography>
                {Array.from(failedFileUrls).map((file) => (
                  <Typography key={file} variant="body2" sx={{ fontSize: '12px', marginBottom: 0.5, color: 'white' }}>
                    • {file}
                  </Typography>
                ))}
              </Box>
            )}
            {Object.keys(fileLoadErrors).length > 0 && (
              <Box sx={{ 
                marginBottom: 2, 
                padding: 2, 
                backgroundColor: 'rgba(255, 0, 0, 0.2)', 
                borderRadius: 1,
                border: '1px solid rgba(255, 0, 0, 0.5)'
              }}>
                <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 'bold', color: 'white' }}>
                  Error Details:
                </Typography>
                {Object.entries(fileLoadErrors).map(([file, error]) => (
                  <Typography key={file} variant="body2" sx={{ fontSize: '12px', marginBottom: 0.5, color: 'white' }}>
                    • {file}: {error}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </FullScreen>
  );
};
